import { useState } from 'react';
import { CuttingModal2 } from './CuttingModal2';
import { LabelCard } from '../components/LabelCard';
import type { LabelCardProps } from '../components/LabelCard';
import { useCuttingStore } from '../state/useCuttingStore';
import { basePartId } from '../lib/cutMapColors';
import { isCompletePart } from '../model/partsUtils';
import { buildOffcutList } from '../lib/offcutList';
import { DEFAULT_MIN_OFFCUT_MM } from '../lib/resultMetrics';

/**
 * Block 14 «Раскрой» — модалка «🏷 Этикетки»: превью этикеток ВСЕХ деталей и
 * остатков на ЭКРАНЕ. По одной этикетке на каждую размещённую деталь (физический
 * кусок) на всех листах и по одной на каждый годный остаток (тот же источник и
 * нумерация, что у карты). Кнопка «Печать PDF» формирует PDF (по странице 60×40 мм
 * на каждую этикетку) и скачивает файл.
 */
export function LabelsDialog({ onClose }: { onClose: () => void }) {
  const lastResult = useCuttingStore((s) => s.lastResult);
  const parts = useCuttingStore((s) => s.document.parts);
  const minOffcutSize = useCuttingStore((s) => s.document.settings.minOffcutSize);
  const projectName = useCuttingStore((s) => s.document.meta.name);
  const material = useCuttingStore((s) => s.document.meta.materialText);

  const threshold = minOffcutSize ?? DEFAULT_MIN_OFFCUT_MM;
  // Номер детали = её позиция в «Список деталей» (тот же фильтр/порядок, что карта).
  const partNumberById = new Map(
    parts.filter(isCompletePart).map((p, i) => [p.id, i + 1] as const),
  );

  // Этикетки деталей: по ОДНОЙ на каждую размещённую деталь (физический кусок) на всех листах.
  const partLabels = (lastResult?.sheets ?? []).flatMap((sheet, si) =>
    sheet.placements.map((pl, pi) => {
      const pid = basePartId(pl.partId);
      const part = parts.find((p) => p.id === pid);
      return {
        key: `p-${si}-${pi}`,
        w: pl.w,
        h: pl.h,
        num: partNumberById.get(pid) ?? 0,
        name: part?.name ?? '',
        edges: part?.edges,
        grooves: part?.grooves,
      };
    }),
  );

  // Этикетки остатков: по списку годных остатков (тот же источник/нумерация, что карта).
  const offcutLabels = lastResult
    ? buildOffcutList(lastResult, threshold).rows.map((r) => ({
        key: `o-${r.number}`,
        w: r.length,
        h: r.width,
        num: r.number,
      }))
    : [];

  // Единый список этикеток — один источник для сетки И PDF.
  type KeyedLabel = LabelCardProps & { key: string };
  const labelItems: KeyedLabel[] = [
    ...partLabels.map(
      (l): KeyedLabel => ({
        key: l.key,
        kind: 'part',
        w: l.w,
        h: l.h,
        num: l.num,
        name: l.name,
        edges: l.edges,
        grooves: l.grooves,
        projectName,
        material,
      }),
    ),
    ...offcutLabels.map(
      (l): KeyedLabel => ({
        key: l.key,
        kind: 'offcut',
        w: l.w,
        h: l.h,
        num: l.num,
        projectName,
        material,
      }),
    ),
  ];

  const [status, setStatus] = useState('');
  const printItems = labelItems.map(({ key: _key, ...props }) => props);
  const handlePrint = () => {
    if (printItems.length === 0) return;
    setStatus('Формирую PDF…');
    // Динамический import: jsPDF тяжёлый и нужен только по клику. Это также не
    // даёт jspdf попасть в eager-граф cutPdf.test (CutMap → LabelsDialog),
    // где vi.mock('jspdf') ронял бы загрузку модуля.
    void import('../lib/labelsPdf')
      .then(({ renderLabelsPdf }) => renderLabelsPdf(printItems))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Этикетки_${new Date().toISOString().slice(0, 10)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setStatus('');
      })
      .catch(() => setStatus('Не удалось сформировать PDF. Попробуйте ещё раз.'));
  };

  return (
    <CuttingModal2
      title="🏷 Этикетки"
      footer={
        <>
          <button type="button" className="sc-modal-btn" onClick={onClose}>
            Закрыть
          </button>
          <button
            type="button"
            className="sc-modal-btn"
            onClick={handlePrint}
            disabled={printItems.length === 0}
          >
            Печать PDF
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {labelItems.map(({ key, ...props }) => (
          <LabelCard key={key} {...props} />
        ))}
      </div>
      <p style={{ fontSize: 11, marginTop: 12 }}>
        Деталей: {partLabels.length}, остатков: {offcutLabels.length}.
      </p>
      {status && (
        <p role="status" aria-live="polite" style={{ fontSize: 11, marginTop: 6 }}>
          {status}
        </p>
      )}
    </CuttingModal2>
  );
}
